import { Combobox } from '@headlessui/react';
import { useRouter } from 'next/dist/client/router';
import { ChangeEvent, Dispatch, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SearchIcon from '../../icons/Search';
import useUpdatePotentialFilterForParam from '../../../lib/hooks/updatePotentialFilterForParam';
import getUnifiedSearchDropdownDataRequest from '../../../lib/api/getUnifiedSearchDropdownDataRequest';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import Spinner from '../../icons/Spinner';

interface SearchBoxProps {
  defaultSelection?: any;
  defaultQuery?: string;
  potentialFilters: any;
  setPotentialFilters: Dispatch<any>;
  setPage: Dispatch<number>;
}

type SearchData = {
  label: string;
  query: any;
};

const SearchBox: React.FC<SearchBoxProps> = ({
  defaultSelection,
  defaultQuery,
  potentialFilters,
  setPotentialFilters,
}) => {
  const router = useRouter();
  const [selectedData, setSelectedData] = useState(defaultSelection || {});
  const [query, setQuery] = useState(defaultQuery || '');
  const [searchQuery, setSearchQuery] = useState('');
  const getSearchDropdownDataThrottled = throttle(
    getUnifiedSearchDropdownDataRequest,
    500,
  );

  // This query calls the API function to get all the possible
  // values for career titles, programs, etc.
  // This value will be cached after the first call.
  const { data: searchDropdownData, isLoading } = useQuery(
    ['search-dropdown-data', searchQuery, 'programs'],
    () => {
      return getSearchDropdownDataThrottled('all', searchQuery);
    },
  );

  // We have to have two separate values: query and searchQuery
  // query is the input's value so that the user can see a working input box
  // searchQuery wll actually trigger the useQuery API hook
  // We debounce searchQuery to minimize renders
  const setSearchQueryDebounced = debounce((query: string) => {
    setSearchQuery(query);
  }, 1000);

  // This function handles the action that occurs when a user
  // selects a result (or click enter)
  const seeResults = (data: any) => {
    if (data.type === 'search') {
      router.push(
        {
          pathname: '/training',
          query: { keyword: data.query },
        },
        undefined,
        { shallow: true },
      );

      return;
    }

    if (data.type === 'training') {
      router.push(`/${data.query}`);
    }

    if (data.type === 'institution') {
      router.push({
        pathname: `/training`,
        query: {
          institution: data.query,
        },
        hash: 'results',
      });
    }

    if (data.type === 'program-careers') {
      router.push({
        pathname: `/training`,
        query: {
          careers: data.query,
        },

        hash: 'results',
      });
    }
  };

  const { updatePotentialFilterForParam } = useUpdatePotentialFilterForParam(
    potentialFilters,
    setPotentialFilters,
  );

  // This function handles changes to the value of the search input
  // and sets it as the query value
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setSearchQueryDebounced(event.target.value);
    updatePotentialFilterForParam({
      paramLabel: 'keyword',
      value: event.target.value as string,
    });
  };

  useEffect(() => {
    const params = router.query;
    if (params.program) {
      setSelectedData(params.program);
    } else if (params.provider) {
      setSelectedData(params.provider as string);
    } else {
      setSelectedData({});
    }
  }, [router]);

  return (
    <section
      data-testid="training-search"
      className="relative z-50 max-w-full mx-auto w-128"
    >
      <Combobox value={selectedData} onChange={seeResults}>
        <SearchIcon className="absolute w-4 h-4 left-3 top-3 stroke-slate-400" />
        <Combobox.Input
          onChange={handleChange}
          displayValue={(data: any) => data?.label || ''}
          className="block w-full p-2 pl-10 my-6 rounded-md border-1 border-border focus:border-search outline-search"
          placeholder="Search for a program"
        />
        <Combobox.Options className="absolute z-10 w-full overflow-scroll bg-white max-h-64 shadow-filter top-12">
          {query.length > 0 && (
            <Combobox.Option
              value={{
                type: 'search',
                query: query,
                label: query,
              }}
            >
              {({ active, selected }) => (
                <li
                  data-testid={query}
                  className={`${
                    active || selected
                      ? 'bg-primary text-white'
                      : 'bg-white text-black'
                  } p-2 hover:bg-primary focus:bg-primary hover:text-white`}
                >
                  {query}
                </li>
              )}
            </Combobox.Option>
          )}
          {Boolean(searchDropdownData && searchDropdownData.length) &&
            searchDropdownData?.map((data: SearchData) => (
              <Combobox.Option
                className="hover:cursor-pointer focus:text-white"
                key={data.label}
                value={data}
              >
                {({ active, selected }) => (
                  <li
                    className={`${
                      active || selected
                        ? 'bg-primary text-white'
                        : 'bg-white text-black'
                    } p-2 hover:bg-primary focus:bg-primary hover:text-white`}
                  >
                    {data.label}
                  </li>
                )}
              </Combobox.Option>
            ))}

          {Boolean(isLoading) && (
            <div className="p-4">
              <Spinner className="w-5 h-5" />
            </div>
          )}
          {Boolean(
            !searchDropdownData?.length && !isLoading && searchQuery,
          ) && (
            <p className="p-4">
              No results match: <i>{searchQuery}</i>
            </p>
          )}
        </Combobox.Options>
      </Combobox>
    </section>
  );
};

export default SearchBox;
